import { IAction, IObjectSignature, IOperation } from 'types';
import { IContainer } from 'types/omjs/project';
import { call, select } from 'typed-redux-saga';
import { sendApplicationForm, sendErrorForm, sendDataUpdateForm } from 'api/submit-application-overlay';
import { getOperationSafe } from 'selectors';
import { SubmitApplicationFormPayload, IFormPayloadFavoriteObject } from 'scenes/submit-application/form-content';
import { ReportErrorFormPayload } from 'scenes/overlay-report-error';
import { IDataUpdateFormPayload } from 'scenes/overlay-data-update/data-update-form';
import { AppService } from 'services/app/app-service';
import { WrongCaptchaError } from 'utils/errors';
import {
    Service,
    latest,
    bindActionsTyped
} from '../service-utils';
import { operation, OperationsService } from '../operations/operations-service';

export type GenericOverlayOperation = IOperation<{ visible: boolean; }>;
export type SubmitApplicationOverlayOperation = IOperation<{ visible: boolean, objects: IObjectSignature[] }>;
export type MapLinkOverlayOperation = IOperation<{ visible: boolean, link: string }>;
export type ObjectLinkOverlayOperation = IOperation<{ visible: boolean, link: string }>;
export type FavoritesExportLayerChooserOperation = IOperation<{ visible: boolean; objects: IObjectSignature[]; }>;
export type UpdateDataOverlay = IOperation<{ visible: boolean; object: IFormPayloadFavoriteObject; }>;

class OverlayWindows extends Service {
    static serviceName = 'OverlayWindowsService';
    static generatedStopActionTypes: string[] = [];
    static generatedWatchers: Function[] = [];

    /**
     * Окно заявки на производство
     */

    @latest
    @operation('SUBMIT_APPLICATION_OVERLAY')
    static* showApplicationOverlay(action: IAction<{objects: IObjectSignature[] }>) {
        let { objects } = action.payload;
        yield OperationsService.setData(
            'SUBMIT_APPLICATION_OVERLAY',
            { visible: true, objects }
        );
    }

    @latest
    @operation('SUBMIT_APPLICATION_OVERLAY')
    static* hideApplicationOverlay() {
        yield OperationsService.clearOperation({ type: '_', payload: 'SUBMIT_APPLICATION_OVERLAY' });
        yield OperationsService.setData(
            'SUBMIT_APPLICATION_OVERLAY',
            { visible: false, objects: [] }
        );
    }

    @latest
    @operation('SUBMIT_APPLICATION_OVERLAY_FORM')
    static* submitApplicationForm(action: IAction<SubmitApplicationFormPayload>) {
        let { payload } = action;
        let form_data = new FormData();

        form_data.append('fio', payload.fio);
        form_data.append('organisation', payload.organisation);
        form_data.append('phone', payload.phone);
        form_data.append('email', payload.email);
        form_data.append('area', payload.area);
        form_data.append('requirements', payload.requirements);
        form_data.append('captcha_key', payload.captcha_key);
        form_data.append('captcha_value', payload.captcha_value);
        form_data.append('objects', payload.objects);
        form_data.append('security_code', payload.security_code);

        let { data } = yield* call(sendApplicationForm, form_data);

        if (!data.SUCCESS) {
            if (data.ERRORS === 'captcha error') {
                yield* AppService.loadNewCaptcha();
                throw new WrongCaptchaError();
            }
        }
    }

    /**
     * Окно для отображение ссылки на карту
     */

    @latest
    @operation('MAP_LINK_OVERLAY')
    static* showMapLinkOverlay(action: IAction<string>) {
        let map_link = action.payload;
        yield OperationsService.setData(
            'MAP_LINK_OVERLAY',
            { visible: true, link: map_link }
        );
    }

    @latest
    @operation('MAP_LINK_OVERLAY')
    static* hideMapLinkOverlay() {
        yield OperationsService.setData(
            'MAP_LINK_OVERLAY',
            { visible: false, link: '' }
        );
    }

    /**
     * Окно для отображение ссылки на объект
     */

    @latest
    @operation('OBJECT_LINK_OVERLAY')
    static* showObjectLinkOverlay(action: IAction<string>) {
        let object_link = action.payload;
        yield OperationsService.setData(
            'OBJECT_LINK_OVERLAY',
            { visible: true, link: object_link }
        );
    }

    @latest
    @operation('OBJECT_LINK_OVERLAY')
    static* hideObjectLinkOverlay() {
        yield OperationsService.setData(
            'OBJECT_LINK_OVERLAY',
            { visible: false, link: '' }
        );
    }

    /**
     * Окно выбора слоя для экспорта объектов из избранного
     */
    @latest
    @operation('EXPORT_OBJECTS_LAYER_CHOOSER_OVERLAY')
    static* showExportLayerChooserOverlay(action: IAction<IObjectSignature[]>) {
        yield OperationsService.setData(
            'EXPORT_OBJECTS_LAYER_CHOOSER_OVERLAY',
            { visible: true, objects: action.payload } as FavoritesExportLayerChooserOperation['data']
        );
    }

    @latest
    @operation('EXPORT_OBJECTS_LAYER_CHOOSER_OVERLAY')
    static* hideExportLayerChooserOverlay() {
        yield OperationsService.setData(
            'EXPORT_OBJECTS_LAYER_CHOOSER_OVERLAY',
            { visible: false, objects: [] } as FavoritesExportLayerChooserOperation['data']
        );
    }

    /**
     * Окно "Сообщить об ошибке"
     */
    @latest
    @operation('REPORT_ERROR_OVERLAY')
    static* showReportErrorOverlay() {
        yield OperationsService.setData(
            'REPORT_ERROR_OVERLAY',
            { visible: true }
        );
    }

    @latest
    @operation('REPORT_ERROR_OVERLAY')
    static* hideReportErrorOverlay() {
        yield OperationsService.clearOperation({ type: '_', payload: 'REPORT_ERROR_OVERLAY' });
        yield OperationsService.setData(
            'REPORT_ERROR_OVERLAY',
            { visible: false }
        );
    }

    @latest
    @operation('SUBMIT_ERROR_OVERLAY')
    static* submitErrorForm(action: IAction<ReportErrorFormPayload>) {
        let { payload } = action;
        let form_data = new FormData();

        form_data.append('name', payload.name);
        form_data.append('email', payload.email);
        form_data.append('message', payload.message);
        form_data.append('captcha_key', payload.captcha_key);
        form_data.append('captcha_value', payload.captcha_value);
        form_data.append('security_code', payload.security_code);

        for (let file of payload.attachments) {
            form_data.append('attachments[]', file);
        }

        let { data } = yield* call(sendErrorForm, form_data);

        if (!data.SUCCESS) {
            if (data.ERRORS === 'captcha error') {
                yield* AppService.loadNewCaptcha();
                throw new WrongCaptchaError();
            }
        }
    }

    /**
     * Окно заявки на обновление данных
     */
    @latest
    @operation('UPDATE_DATA_OVERLAY')
    static* showUpdateDataOverlay(action: IAction<IFormPayloadFavoriteObject>) {
        yield OperationsService.setData(
            'UPDATE_DATA_OVERLAY',
            { visible: true, object: action.payload }
        );
    }

    @latest
    @operation('UPDATE_DATA_OVERLAY')
    static* hideUpdateDataOverlay() {
        yield OperationsService.clearOperation({ type: '_', payload: 'UPDATE_DATA_OVERLAY' });
        yield OperationsService.setData(
            'UPDATE_DATA_OVERLAY',
            { visible: false }
        );
    }

    @latest
    @operation('SUBMIT_DATA_UPDATE_OVERLAY')
    static* submitDataUpdateForm(action: IAction<IDataUpdateFormPayload>) {
        let { payload } = action;
        let form_data = new FormData();

        form_data.append('fio', payload.fio);
        form_data.append('email', payload.email);
        form_data.append('message', payload.message);
        form_data.append('captcha_key', payload.captcha_key);
        form_data.append('captcha_value', payload.captcha_value);
        form_data.append('security_code', payload.security_code);
        form_data.append('object', payload.object);

        for (let file of payload.attachments) {
            form_data.append('attachments[]', file);
        }

        let { data } = yield* call(sendDataUpdateForm, form_data);

        if (!data.SUCCESS) {
            if (data.ERRORS === 'captcha error') {
                yield* AppService.loadNewCaptcha();
                throw new WrongCaptchaError();
            }
        }
    }

    /**
     * Окно авторизации
     */
    @latest
    @operation('LOGIN_OVERLAY')
    static* showLoginOverlay() {
        yield OperationsService.setData(
            'LOGIN_OVERLAY',
            { visible: true }
        );
    }

    @latest
    @operation('LOGIN_OVERLAY')
    static* hideLoginOverlay() {
        yield OperationsService.setData(
            'LOGIN_OVERLAY',
            { visible: false }
        );
    }
}


export const overlayWindowsService: OverlayWindows = new OverlayWindows();
export const actions = bindActionsTyped(OverlayWindows, overlayWindowsService);
overlayWindowsService.actions = actions;
